import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, ChatCircle } from '@phosphor-icons/react';
import Breadcrumbs from '../components/Breadcrumbs';
import type { LibraryTopic } from '@researcher/shared';
import AppNav from '../components/AppNav';
import AppFooter from '../components/AppFooter';
import PageMeta from '../components/PageMeta';
import TopicSectionRenderer from '../components/library/TopicSectionRenderer';
import TopicTableOfContents from '../components/library/TopicTableOfContents';
import BookmarkButton from '../components/library/BookmarkButton';
import LoginModal from '../components/LoginModal';
import { useAuth } from '../contexts/AuthContext';
import { getTopic, getBookmarks, recordTopicView } from '../utils/library';

export default function TopicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { currentUser } = useAuth();
  const [topic, setTopic] = useState<LibraryTopic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookmarkId, setBookmarkId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);

    getTopic(slug)
      .then(data => {
        setTopic(data);
        if (data.sections.length > 0) {
          setActiveSection(data.sections[0].type);
        }
      })
      .catch(err => setError(err.message || 'Failed to load topic'))
      .finally(() => setLoading(false));
  }, [slug]);

  // Record topic view (fire-and-forget)
  useEffect(() => {
    if (!currentUser || !slug) return;
    recordTopicView(slug);
  }, [currentUser, slug]);

  // Load bookmarks to check if this topic is bookmarked
  useEffect(() => {
    if (!currentUser || !topic) return;
    getBookmarks()
      .then(bookmarks => {
        const match = bookmarks.find(b => b.topicSlug === topic.slug);
        if (match) setBookmarkId(match.id);
      })
      .catch(console.error);
  }, [currentUser, topic]);

  // Scroll spy for table of contents
  useEffect(() => {
    if (!topic) return;
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const sectionType = entry.target.id.replace('section-', '');
            setActiveSection(sectionType);
          }
        }
      },
      { rootMargin: '-100px 0px -60% 0px' }
    );

    for (const section of topic.sections) {
      const el = document.getElementById(`section-${section.type}`);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [topic]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
        <AppNav />
        <div className="max-w-4xl mx-auto px-4 pt-24 pb-16">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !topic) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
        <AppNav />
        <div className="max-w-3xl mx-auto px-4 pt-24 pb-16 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error || 'Topic not found'}</p>
          <Link to="/research" className="text-pink-500 hover:text-pink-600 font-medium text-sm">
            Back to Library
          </Link>
        </div>
      </div>
    );
  }

  // Collect all unique citations across sections
  const allCitations = topic.sections.flatMap(s => s.citations);
  const uniqueCitations = allCitations.filter(
    (c, i) => allCitations.findIndex(cc => cc.pubmedId === c.pubmedId) === i
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
      <PageMeta
        title={`${topic.title} — ResearcHer Library`}
        description={topic.patientSummary}
        path={`/research/topics/${topic.slug}`}
      />
      <AppNav onLoginOpen={() => setIsLoginOpen(true)} />

      <main className="pt-16 pb-16 md:pb-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          {/* Breadcrumb */}
          <Breadcrumbs current={topic.title} />

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-100 dark:bg-pink-950 text-pink-700 dark:text-pink-300 font-medium">
                    {topic.category.replace('-', ' ')}
                  </span>
                  {topic.conditionTags.map(tag => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                      {tag.replace(/-/g, ' ')}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                  {topic.title}
                </h1>
                {topic.subtitle && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">{topic.subtitle}</p>
                )}
              </div>
              <BookmarkButton
                topicId={topic.id}
                topicSlug={topic.slug}
                topicTitle={topic.title}
                bookmarkId={bookmarkId}
                onToggle={setBookmarkId}
              />
            </div>

            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <Clock size={14} /> {topic.readTimeMinutes} min read
              </span>
              <span>Updated {formatDate(topic.lastUpdated)}</span>
              <span>v{topic.version} &middot; {topic.sourceDocumentCount} sources</span>
            </div>
          </div>

          {/* Patient summary */}
          <div className="bg-pink-50 dark:bg-pink-950/30 rounded-xl p-4 mb-8 border border-pink-100 dark:border-pink-900">
            <h3 className="text-xs font-semibold text-pink-700 dark:text-pink-300 uppercase tracking-wider mb-1">
              At a Glance
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {topic.patientSummary}
            </p>
          </div>

          {/* Content with sidebar */}
          <div className="lg:grid lg:grid-cols-[1fr_200px] lg:gap-8">
            <div>
              {/* Mobile TOC only */}
              <div className="lg:hidden">
                <TopicTableOfContents
                  sections={topic.sections}
                  activeSection={activeSection}
                />
              </div>

              {/* Sections */}
              <div className="space-y-8">
                {topic.sections.map(section => (
                  <TopicSectionRenderer key={section.type} section={section} />
                ))}
              </div>

              {/* Citations */}
              {uniqueCitations.length > 0 && (
                <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-base font-bold text-gray-800 dark:text-gray-100 mb-4">
                    Sources ({uniqueCitations.length})
                  </h3>
                  <ol className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
                    {uniqueCitations.map((c, i) => (
                      <li key={c.pubmedId} className="flex gap-2">
                        <span className="text-gray-400 flex-shrink-0">{i + 1}.</span>
                        <div>
                          <a
                            href={`https://pubmed.ncbi.nlm.nih.gov/${c.pubmedId}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pink-600 dark:text-pink-400 hover:underline"
                          >
                            {c.title}
                          </a>
                          <span className="text-gray-400 ml-1">
                            — {c.authors}, {c.journal} ({c.year})
                          </span>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Ask Romy CTA */}
              <div className="mt-8 bg-gradient-to-r from-pink-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-xl p-5 border border-pink-200 dark:border-pink-800 text-center">
                <ChatCircle size={28} className="text-pink-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-1">
                  Have questions about {topic.title.toLowerCase()}?
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Ask Romy for a personalized deep dive with full citations.
                </p>
                <Link
                  to="/chat"
                  className="inline-block px-5 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-medium rounded-lg text-sm hover:from-pink-600 hover:to-purple-600 transition-all"
                >
                  Ask Romy
                </Link>
              </div>

              {/* Related topics */}
              {topic.relatedTopicSlugs && topic.relatedTopicSlugs.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-3">Related Topics</h3>
                  <div className="flex gap-2 flex-wrap">
                    {topic.relatedTopicSlugs.map(slug => (
                      <Link
                        key={slug}
                        to={`/research/topics/${slug}`}
                        className="px-3 py-1.5 bg-white dark:bg-gray-800 rounded-lg text-xs text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-pink-300 dark:hover:border-pink-700 transition-colors"
                      >
                        {slug.replace(/-/g, ' ')}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Desktop sidebar TOC */}
            <aside className="hidden lg:block self-start sticky top-24">
              <TopicTableOfContents
                sections={topic.sections}
                activeSection={activeSection}
              />
            </aside>
          </div>
        </div>
      </main>

      <AppFooter />
      {isLoginOpen && <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />}
    </div>
  );
}
